// src/components/ChangePasswordModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const ChangePasswordModal = ({ open, handleClose }) => {
  const { user } = useAuth(); // Get user from context
  const { t } = useTranslation(); // Extract 't' for translations
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState('');

  useEffect(() => {
    if (user && user.identities) {
      // Check if user has an email provider identity
      const emailIdentity = user.identities.find(identity => identity.provider === 'email');
      setHasPassword(!!emailIdentity);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Restricciones como en ActualizarContraseña.jsx
  const passwordLengthValid = formData.newPassword.length >= 8 && formData.newPassword.length <= 20;
  const passwordSpecialValid = /[!ºª|@·#$~%¬/()='?¡¿`^+[*{}¨Ç´\-_.:,;]/.test(formData.newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const { currentPassword, newPassword, confirmNewPassword } = formData;

    // Validaciones igual que ActualizarContraseña.jsx
    if (!newPassword || !confirmNewPassword) {
      setError(t('changePassword.completeAllFields'));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError(t('changePassword.passwordsDoNotMatch'));
      return;
    }

    if (!passwordLengthValid) {
      setError(t('validation.passwordLength') || 'La contraseña debe tener entre 8 y 20 caracteres.');
      return;
    }

    if (!passwordSpecialValid) {
      setError(t('validation.passwordSpecialChar') || 'La contraseña debe contener al menos un carácter especial.');
      return;
    }

    if (hasPassword && !currentPassword) {
      setError(t('changePassword.enterCurrentPassword'));
      return;
    }

    setLoading(true);

    try {
      if (hasPassword) {
        // Re-authenticate user with current password
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        });

        if (signInError) {
          setError(t('changePassword.currentPasswordIncorrect'));
          setLoading(false);
          return;
        }
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setError(updateError.message || t('changePassword.updateError'));
        setLoading(false);
        return;
      }

      setSuccess(t('changePassword.updateSuccess'));
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });

      // Optional: Close modal after a short period
      setTimeout(() => {
        handleClose();
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(t('changePassword.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  // Validar contraseña actual al perder el foco
  const handleCurrentPasswordBlur = async () => {
    setCurrentPasswordError('');
    if (hasPassword && formData.currentPassword) {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: formData.currentPassword,
      });
      if (signInError) {
        setCurrentPasswordError(t('changePassword.currentPasswordIncorrect'));
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('changePassword.title')}</DialogTitle>
      <DialogContent>
        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          {hasPassword && (
            <>
              <TextField
                label={t('changePassword.currentPassword')}
                name="currentPassword"
                type="password"
                value={formData.currentPassword}
                onChange={handleChange}
                onBlur={handleCurrentPasswordBlur}
                fullWidth
                margin="normal"
                required
                error={Boolean(currentPasswordError)}
                helperText={currentPasswordError}
              />
            </>
          )}
          <TextField
            label={t('changePassword.newPassword')}
            name="newPassword"
            type="password"
            value={formData.newPassword}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            error={formData.newPassword.length > 0 && (!passwordLengthValid || !passwordSpecialValid)}
            helperText={
              formData.newPassword.length > 0 && (!passwordLengthValid || !passwordSpecialValid)
                ? ( !passwordLengthValid
                    ? (t('validation.passwordLength') || 'La contraseña debe tener entre 8 y 20 caracteres.')
                    : (t('validation.passwordSpecialChar') || 'La contraseña debe contener al menos un carácter especial.')
                  )
                : ''
            }
          />
          <TextField
            label={t('changePassword.confirmNewPassword')}
            name="confirmNewPassword"
            type="password"
            value={formData.confirmNewPassword}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            error={
              formData.confirmNewPassword.length > 0 &&
              formData.confirmNewPassword !== formData.newPassword
            }
            helperText={
              formData.confirmNewPassword.length > 0 &&
              formData.confirmNewPassword !== formData.newPassword
                ? t('changePassword.passwordsDoNotMatch')
                : ''
            }
          />

          {/* Checkboxes de Validación como en ActualizarContraseña.jsx */}
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={passwordLengthValid}
                  disabled
                  color="primary"
                  sx={{
                    '&.Mui-disabled': {
                      color: passwordLengthValid ? '#1976d2' : 'rgba(0, 0, 0, 0.26)',
                    },
                  }}
                />
              }
              label={t('register.passwordRequirement1') || 'Entre 8 y 20 caracteres'}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={passwordSpecialValid}
                  disabled
                  color="primary"
                  sx={{
                    '&.Mui-disabled': {
                      color: passwordSpecialValid ? '#1976d2' : 'rgba(0, 0, 0, 0.26)',
                    },
                  }}
                />
              }
              label={t('register.passwordRequirement2') || 'Al menos un carácter especial'}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('changePassword.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          color="primary"
        >
          {loading ? <CircularProgress size={24} /> : t('changePassword.changePassword')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;
